import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useState } from "react";
import { GetStringValue } from "../../utilities/utilities";
import { ITemplate } from "../../types/types";

const useStyles = makeStyles((theme: Theme) => ({
	button: {
		background: "#006B86",
		size: "small",
		margin: theme.spacing(1),
		color: "white",
	}
}));

interface NewTemplateDialogProps {
	open: boolean;
	onSave: (newTemplate: ITemplate) => void;
	onClose: () => void;
}

const NewTemplateDialog = ({ open, onSave, onClose }: NewTemplateDialogProps) => {
	const classes = useStyles();
	const [templateNameError, setTemplateNameError] = useState('');
	const [canSave, setCanSave] = useState(false);
	const [newTemplateName, setNewTemplateName] = useState('');
	const [templateExistsMessage, setTemplateExistsMessage] = useState('This template name is already used. Please enter another name.');

	useEffect(() => {
		const getErrorMessage = async () => {
			const theString = await GetStringValue("templateNameAlreadyUsed");

			if (theString) {
				setTemplateExistsMessage(theString.value || templateExistsMessage);
			}
		};

		getErrorMessage();
	}, []);

	const resetState = () => {
		setNewTemplateName('');
		setCanSave(false);
		setTemplateNameError('');
	}

	const handleClose = (event?: React.MouseEventHandler<HTMLButtonElement> | any, reason?: string) => {
		if (reason === 'backdropClick') {
			return;
		}

		resetState();
		onClose();
	};

	const handleTemplateNameValidation = () => {
		if (templateNameError)
			setTemplateNameError('');

		if (newTemplateName && newTemplateName.trim().length > 0) {
			setCanSave(true);
		} else {
			setCanSave(false);
		}
	};

	const saveTemplate = (templateName: string) => {
		fetch('api/template/createTemplate', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ templateName: newTemplateName }),
		})
			.then(response => response.json())
			.then(data => {
				if (data) {
					if (data.error) {
						setCanSave(false);
						setTemplateNameError(data.error);
						return;
					}

					resetState();
					onSave(data as ITemplate);
				} else {
					setCanSave(false);
					setTemplateNameError("Nothing returned from the server.  If this continues, please contact support.");
				}
			})
			.catch((error) => {
				console.error('Error:', error);
				setCanSave(false);
				setTemplateNameError(error);
			});
	};

	const handleSave = (e: { preventDefault: () => void; }) => {
		e.preventDefault();

		// call API to check if template name already exists

		fetch('api/Template/CheckTemplateName', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ templateName: newTemplateName }),
		})
			.then(response => response.json())
			.then(data => {
				if (data) {
					if (data.found) {
						setCanSave(false);
						setTemplateNameError(templateExistsMessage);
						return;
					}

					if (data.error) {
						setCanSave(false);
						setTemplateNameError(data.error);
						return;
					}

					saveTemplate(newTemplateName);
				} else {
					setCanSave(false);
					setTemplateNameError(templateExistsMessage);
				}
			})
			.catch((error) => {
				console.error('Error:', error);
				setCanSave(false);
				setTemplateNameError(error);
			});
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth={false} PaperProps={{ style: { width: '32rem' } }}>
			<DialogTitle>
				<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
					<span>Create New Template</span>
					<Button onClick={handleClose} style={{ border: 'none', minWidth: '30px', color: 'gray' }}>X</Button>
				</div>
			</DialogTitle>

			<DialogContent>
				<TextField
					autoFocus
					margin="normal"
					label="New Template Name"
					fullWidth
					value={newTemplateName}
					onChange={(e) => setNewTemplateName(e.target.value)}
					variant="standard"
					onKeyUp={handleTemplateNameValidation}
					error={!!templateNameError}
					helperText={templateNameError}
					InputLabelProps={{
						shrink: true,
						style: {
							color: "black",
						}
					}}
				/>
			</DialogContent>

			<DialogActions>
				<Button variant="outlined" onClick={handleClose}>Cancel</Button>
				<Button
					className={classes.button}
					variant="contained"
					onClick={handleSave}
					disabled={!canSave}
					sx={{
						"&:focus": {
							backgroundColor: "#253746 !important", // Keep the defonstackground color on focus
						},
					}}>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default NewTemplateDialog;